import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import Combobox from './Combobox.jsx'
import { cachedGet } from '../lib/api.js'

/**
 * Appointment-TYPE picker, shared by the Call list + dashboard.
 *
 * The appointmentList catalog (GET /api/appointment-types → id + type + detail) tags
 * a call with the appointment type it concerns. One cached fetch feeds every picker;
 * Combobox is label-only, so we map id ⇄ "type · detail" here.
 */
const asList = (r) => (Array.isArray(r) ? r : Array.isArray(r?.items) ? r.items : [])

export function useApptTypes() {
  const [rows, setRows] = useState([])
  useEffect(() => {
    cachedGet('/api/appointment-types')
      .then((r) => setRows(asList(r).map((t) => ({
        id: String(t.id ?? t.appointmentlist_id),
        label: [t.type, t.detail].filter(Boolean).join(' · ') || `#${t.id}`,
      })).filter((t) => t.id && t.id !== 'undefined')))
      .catch(() => {})
  }, [])
  const labels = useMemo(() => rows.map((r) => r.label), [rows])
  const idToLabel = useMemo(() => new Map(rows.map((r) => [r.id, r.label])), [rows])
  const labelToId = useMemo(() => new Map(rows.map((r) => [r.label, r.id])), [rows])
  return { rows, labels, idToLabel, labelToId }
}

// `value`/`onChange` speak in ids ('' = none); Combobox speaks labels, translated here.
export function ApptTypeCombo({ value, onChange, placeholder = 'ประเภทนัด…' }) {
  const { labels, idToLabel, labelToId } = useApptTypes()
  return (
    <Combobox
      white
      options={labels}
      value={value ? (idToLabel.get(String(value)) || '') : ''}
      onChange={(label) => onChange(label ? (labelToId.get(label) || '') : '')}
      placeholder={placeholder}
    />
  )
}

// Fixed-width filter variant with a clear (×) affordance.
export function ApptTypeFilter({ value, onChange, width = 'w-[210px]', placeholder = 'ทุกประเภทนัด' }) {
  return (
    <div className={`relative ${width}`}>
      <ApptTypeCombo value={value} onChange={onChange} placeholder={placeholder} />
      {value ? (
        <button type="button" onClick={() => onChange('')} title="ล้างตัวกรอง"
          className="absolute right-9 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
          <X size={13} />
        </button>
      ) : null}
    </div>
  )
}
